import m, {MithrilComponent} from '@bitstillery/mithril'

import type {CmsComponentMeta} from '@bitstillery/common/cms/catalog_meta'

export const gridMeta = {
    tier: 'layout',
    summary: 'Responsive grid that arranges its children (usually `Card`s) into auto-fitting columns.',
    when_to_use:
        'Pure layout — it owns arrangement, not a heading. Wrap in a `Section` for a title. Set `min` to line cards out at a fixed width instead of stretching them.',
    example: `
    <Grid min="16rem">
        <Card description="1,200 offers in stock" title="Whisky" />
        <Card description="480 offers in stock" title="Rum" />
    </Grid>
    `,
    variants: [
        {
            prop: 'min',
            label: 'no min (auto-fit)',
            jsx: `
            <Grid>
                <Card description="1,200 offers in stock" title="Whisky" />
                <Card description="480 offers in stock" title="Rum" />
            </Grid>
            `,
        },
    ],
} satisfies CmsComponentMeta

export interface GridAttrs {
    /**
     * Fixed column width for the grid (default: unset → auto-fit fills the row). When set, the grid
     * uses `auto-fill`, so an under-filled row lines its cards out to the left at this width instead
     * of stretching them across the full width.
     */
    min?: string
}

/**
 * Responsive grid that arranges its children (typically `Card`s) into auto-fitting columns. Pure
 * layout — it owns arrangement only, not a heading or content. Wrap in a `Section` for a title.
 */
export class Grid extends MithrilComponent<GridAttrs> {
    view(vnode: m.Vnode<GridAttrs>) {
        const min = typeof vnode.attrs.min === 'string' && vnode.attrs.min.trim() !== '' ? vnode.attrs.min.trim() : undefined
        const style = min ? {gridTemplateColumns: `repeat(auto-fill, minmax(min(${min}, 100%), 1fr))`} : undefined
        return (
            <div className='c-grid' style={style}>
                {vnode.children}
            </div>
        )
    }
}
